import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import AlertIcon from "@mui/icons-material/WarningAmberOutlined";
import CertIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import MessageIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckIcon from "@mui/icons-material/Check";
import { ROUTES } from "../shared/classData";
import { Num, Ring, useReady } from "../admin/adminCharts";
import "./studentDashboard.css";

const ICONS = { alert: AlertIcon, cert: CertIcon, msg: MessageIcon };
const STATUS_TEXT = { act: "Active", soon: null, exp: "Expired" };

function Countdown({ to }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  let s = Math.max(0, Math.floor((to - now) / 1000));
  const d = Math.floor(s / 86400); s %= 86400;
  const h = Math.floor(s / 3600); s %= 3600;
  const m = Math.floor(s / 60); s %= 60;
  return <div className="sd-cd" aria-label="Time until next session">{[[d, "D"], [h, "H"], [m, "M"], [s, "S"]].map(([v, l]) => <span key={l}><b>{String(v).padStart(2, "0")}</b><small>{l}</small></span>)}</div>;
}

function Hero({ data, ready }) {
  const { next, credits, hours, certCounts, header } = data;
  const tiles = [
    { l: "Next session", testId: "student-next-session", wide: true,
      v: <span style={{ fontSize: 20, lineHeight: 1.2 }}>{next ? next.title : "Nothing scheduled"}</span>,
      s: next ? <><b>{next.when}</b> · {next.time}{next.place ? ` · ${next.place}` : ""}</> : "Enroll in a class to see it here",
      cd: next ? <Countdown to={next.at.getTime()} /> : null },
    { l: "Total credits", v: <Num to={credits.earned} delay={60} />,
      s: credits.required ? <><span className="dl">{credits.earned} of {credits.required}</span>{credits.met ? "requirement met" : "required"}</> : "Active certifications",
      viz: credits.met ? <Ring pct={100} size={44} sw={5} color="var(--color-accent-lime)" delay={350}><span style={{ color: "var(--color-accent-lime)", display: "grid" }}><CheckIcon sx={{ fontSize: 16 }} /></span></Ring> : null },
    { l: "Hours logged", v: <><Num to={hours.value} /><small>hrs</small></>, s: "From completed classes",
      viz: <div><div className="yr" aria-hidden="true"><b style={{ left: `${hours.yearPct}%` }}></b></div></div> },
    { l: "Certifications", v: <Num to={certCounts.total} delay={180} />,
      s: <><span className="dl w">{certCounts.expired}</span>expired · {certCounts.active} active</>,
      viz: <div className="split" aria-hidden="true"><i style={{ flexGrow: ready ? certCounts.active : 0, background: "var(--color-accent-lime)" }}></i><i style={{ flexGrow: ready ? certCounts.expired : 0, background: "var(--color-accent-pink)" }}></i></div> },
  ];
  return (
    <header className="sd-hero"><div className="sd-hero-in">
      <div className="t"><span className="eb">{header.eyebrow}</span><h1>{header.greeting}</h1></div>
      <div className="hm4">{tiles.map((k, i) => <div className="hk" key={k.l} data-testid={k.testId} style={{ animationDelay: `${i * 60}ms` }}>
        <span className="eb">{k.l}</span>
        <div className="row"><span className="v">{k.v}</span>{k.viz}</div>
        {k.cd}
        <span className="sub">{k.s}</span></div>)}</div>
    </div></header>
  );
}

function Certs({ data, ready }) {
  const { certs, timeline, navigate } = data;
  return (
    <section className="sd-c s8" data-testid="student-certs" aria-label="My certifications" style={{ animationDelay: "140ms" }}>
      <div className="sd-ch"><h3>My certifications</h3><span className="eb" style={{ opacity: 0.5 }}>{timeline.label}</span>
        <a className="sp" href="/certifications" style={{ fontSize: 13 }} onClick={(e) => { e.preventDefault(); navigate(ROUTES.certifications); }}>All certifications</a></div>
      {certs.length === 0 ? <p className="empty">No certifications on file yet.</p> : (
        <div className="tl">
          <div className="nowline" style={{ "--p": timeline.nowPct / 100 }}><span>Today</span></div>
          <div className="tl-axis"><span></span><div className="months" style={{ gridTemplateColumns: "repeat(13,1fr)" }}>{timeline.months.map((m, i) => <span key={i}>{m}</span>)}</div><span></span></div>
          {certs.map((c, i) => <div key={c.id} className="tl-row">
            <div className="nm"><b>{c.name}</b><small>{[c.org, c.number].filter(Boolean).join(" · ")}</small></div>
            <div className="track-l"><div className={`span ${c.status}`} style={{ left: 0, width: ready ? `${c.exp ? timeline.pos(c.exp) : 100}%` : 0, transitionDelay: `${i * 90}ms` }}></div></div>
            <div className="st"><span className={`stp ${c.status}`}>{STATUS_TEXT[c.status] ?? `${c.days} days left`}</span>
              <small>{!c.exp ? "No expiration" : c.status === "exp" ? `Expired ${c.exp.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : `Valid to ${c.exp.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}</small></div>
          </div>)}
        </div>
      )}
    </section>
  );
}

function Attention({ data }) {
  const { attention } = data;
  return (
    <section className="sd-c s4" data-testid="student-attention" aria-label="Needs attention" style={{ animationDelay: "80ms" }}>
      <div className="sd-ch"><h3>Needs attention</h3>{attention.length > 0 && <span className="delta dn sp">{attention.length}</span>}</div>
      {attention.length === 0
        ? <div className="clear"><span className="ck"><CheckIcon fontSize="small" /></span><p>You are all caught up</p><small>Expired or expiring certifications and unread messages will show up here.</small></div>
        : <div>{attention.map((a) => {
          const Icon = ICONS[a.icon] || AlertIcon;
          return <div key={a.id} className="ar"><span className={`ic ${a.tone}`}><Icon fontSize="small" /></span><div><p>{a.title}</p><small>{a.detail}</small></div><button type="button" className="go" onClick={a.onClick}>{a.cta}</button></div>;
        })}</div>}
    </section>
  );
}

function Upcoming({ data }) {
  const { upcoming, navigate } = data;
  return (
    <section className="sd-c s12" data-testid="student-upcoming" aria-label="Upcoming sessions" style={{ animationDelay: "200ms" }}>
      <div className="sd-ch"><h3>Upcoming sessions</h3><span className="eb" style={{ opacity: 0.5 }}>Next {upcoming.length || 5} sessions</span>
        <a className="sp" href={ROUTES.myClasses} style={{ fontSize: 13 }} onClick={(e) => { e.preventDefault(); navigate(ROUTES.myClasses); }}>My classes</a></div>
      {upcoming.length === 0 ? <p className="empty">No upcoming sessions.</p> : (
        <div className="ups">{upcoming.map((u, i) => <button type="button" key={u.id} className={`up${i === 0 ? " nx" : ""}`} style={{ animationDelay: `${240 + i * 50}ms` }} onClick={u.onClick}>
          <span className="d"><small>{u.month}</small><b>{u.day}</b></span>
          <span className="tm"><i style={{ background: u.color }}></i>{u.time}</span>
          <p>{u.title}</p>{u.place && <span className="m">{u.place}</span>}
          <span className="wh">{u.when}</span></button>)}</div>
      )}
    </section>
  );
}

/** Certification-track regions, shown only when the tenant enables the certificationTrack feature. */
function Track({ track }) {
  return <>
    <section className="sd-c s6" aria-label="Current Track" data-testid="student-track" style={{ animationDelay: "100ms" }}>
      <div className="sd-ch"><h3>Current Track</h3></div>
      <div className="trk"><div><p>{track.label}</p><small>{track.next}</small></div></div>
    </section>
    <section className="sd-c s6" aria-label="Track Progress" style={{ animationDelay: "120ms" }}>
      <div className="sd-ch"><h3>Track Progress</h3></div>
      <div className="trk"><Ring pct={track.pct} size={56} sw={6} color="var(--color-primary)" /><div><p>{track.pct}% complete</p></div></div>
    </section>
  </>;
}

/**
 * Student landing dashboard.
 * @param {{ data: object }} props view model from buildStudentModel
 */
export default function StudentDashboard({ data }) {
  const ready = useReady(300);
  if (data.loading) return <Box sx={{ display: "grid", placeItems: "center", minHeight: 240 }}><CircularProgress /></Box>;
  return (
    <div className="sd" data-testid="student-dashboard">
      <Hero data={data} ready={ready} />
      <main className="sd-wrap">
        <div className="sd-grid">
          {data.track && <Track track={data.track} />}
          <Certs data={data} ready={ready} />
          <Attention data={data} />
          <Upcoming data={data} />
        </div>
      </main>
    </div>
  );
}
